import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import TabsPageLayout from 'src/components/TabsPageLayout';
import { PORTAL_ROUTE } from 'src/constants/routes';
import {
  PACKAGED_APPS_STATUS_CATALOG_TARGET,
  PACKAGED_APPS_STATUS_OPTIONS,
  trustedStatus,
  WDAC_PACKAGED_APP_PREVIOUS_PATH,
  wdacPackagedAppTabs,
} from '../constants';
import useSubscribeStorage from 'src/hooks/useSubscribeStorage';
import { DEFAULT_ENVIRONMENT_ID } from 'src/constants/storage';
import { WDAC_PACKAGED_APP_GENERAL_QUERY_KEY } from 'src/constants/query';
import { Box } from '@mui/material';
import { useSnackbar } from 'notistack';
import { pagesText } from 'src/constants/pagesText';
import { Loader } from 'src/components';
import UpdateStatusModal from 'src/features/appStatus/UpdateStatusModal';
import usePackagedAppGeneral from '../../../../../queries/packagedAppDetails/useGetPackagedAppGeneral';
import WDACPackagedAppService from '../../../../../services/wdac-packaged-app.service';

const PackagedAppsTabs = () => {
  const { packagedAppId } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const customerEnvId = useSubscribeStorage(DEFAULT_ENVIRONMENT_ID);
  const queryClient = useQueryClient();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const { isLoading: packagedAppIsLoading, data: appData } = usePackagedAppGeneral({
    packagedAppId,
  });

  const { mutate: updateStatus, isLoading: statusLoading } = useMutation(
    WDACPackagedAppService.updateGlobalPackagedAppStatus,
    {
      onError: () => {
        enqueueSnackbar(pagesText.somethingWentWrong, {
          variant: 'error',
          autoHideDuration: 3000,
        });
      },
      // Writing through the cache rather than into local state keeps the header and the general
      // tab, which reads the same query, showing the same thing.
      onSuccess: data => {
        queryClient.setQueryData([WDAC_PACKAGED_APP_GENERAL_QUERY_KEY, packagedAppId], previous =>
          previous
            ? {
                ...previous,
                status: data?.status,
                notes: data?.notes,
                statusUpdatedOn: data?.statusUpdatedOn,
              }
            : previous,
        );
        enqueueSnackbar('Data has been updated successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        setIsEditModalOpen(false);
      },
    },
  );

  const handleSave = (newStatus, note) => {
    updateStatus({
      customerEnvId,
      globalPackagedAppId: packagedAppId,
      status: newStatus,
      notes: note,
    });
  };

  // A system-signed trusted app is not ours to retrust, so the whole edit path stays closed.
  const editDisabled = appData?.status === trustedStatus && appData?.signatureKind === 'System';

  const openEditModal = useCallback(() => setIsEditModalOpen(true), []);
  const outletContext = useMemo(
    () => ({ onEditStatus: openEditModal, editDisabled }),
    [openEditModal, editDisabled],
  );

  const backNavigationPath = useMemo(() => {
    const storedPath = sessionStorage.getItem(WDAC_PACKAGED_APP_PREVIOUS_PATH);
    return storedPath || `${PORTAL_ROUTE}/wdac-results/packaged-apps`;
  }, []);

  if (packagedAppIsLoading) {
    return <Loader />;
  }
  return (
    <div>
      <TabsPageLayout
        hasBackButton
        // Status is acted on from the general tab rather than the header.
        title={<Box component="span">{appData?.displayName || 'No Display Name'}</Box>}
        tabs={wdacPackagedAppTabs(packagedAppId, appData?.affectedDevicesCount)}
        backNavigationPath={backNavigationPath}
        wrapperProps={{
          sx: {
            maxWidth: '100%',
          },
        }}
        titleProps={{
          fontSize: '1.4rem',
        }}
        outletContext={outletContext}
      />
      <UpdateStatusModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        summary={appData?.displayName}
        currentStatus={appData?.status}
        currentNote={appData?.notes}
        currentDate={appData?.statusUpdatedOn}
        statusOptions={PACKAGED_APPS_STATUS_OPTIONS}
        catalogTarget={PACKAGED_APPS_STATUS_CATALOG_TARGET}
        disableStatusSelect={editDisabled}
        prefillNote
        onSave={handleSave}
        loading={statusLoading}
      />
    </div>
  );
};

export default PackagedAppsTabs;
